/**
 * Step2Business — second step of the signup wizard.
 *
 * Sections (top to bottom):
 *   A. Whose business is this? — radio: Mine | Partnered with the business owner
 *      When "Partnered", rep fields appear (first, last, phone)
 *   B. SS4 upload — required in Phase 1 pilot. Backend still supports the
 *      email_later / manual paths; UI cards are hidden until pilot ends.
 *      (i) info icon opens SS4InfoModal with sample mockup
 *   C. Your billing address — street/city/state/zip (always shown)
 */

import { useEffect, useState } from 'react';
import { Info, Upload } from 'lucide-react';
import { getMe, submitStep2, uploadSs4 } from '../lib/api';
import type { Step2Payload } from '../lib/types';
import SS4InfoModal from '../components/SS4InfoModal';

interface Props {
  navigate: (path: string) => void;
}

type Ss4Choice = 'upload' | 'email_later' | 'manual';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

interface FormState {
  has_representative: 'no' | 'yes';
  rep_first: string;
  rep_last: string;
  rep_phone: string;

  ss4_choice: Ss4Choice | '';
  ss4_file: File | null;

  // Manual-path fields
  legal_business_name: string;
  ein: string;
  ein_issued_date: string;
  business_street: string;
  business_city: string;
  business_state: string;
  business_zip: string;

  // Billing address (always)
  applicant_street: string;
  applicant_city: string;
  applicant_state: string;
  applicant_zip: string;
}

const EMPTY_FORM: FormState = {
  has_representative: 'no',
  rep_first: '',
  rep_last: '',
  rep_phone: '',
  ss4_choice: 'upload',
  ss4_file: null,
  legal_business_name: '',
  ein: '',
  ein_issued_date: '',
  business_street: '',
  business_city: '',
  business_state: '',
  business_zip: '',
  applicant_street: '',
  applicant_city: '',
  applicant_state: '',
  applicant_zip: '',
};

export default function Step2Business({ navigate }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSs4Info, setShowSs4Info] = useState(false);
  const [firstName, setFirstName] = useState('');

  // Bootstrap: confirm session is valid + pre-fill if Step 2 was partially done
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getMe();
      if (cancelled) return;
      if (!res.success || !res.prospect) {
        navigate('/portal/login');
        return;
      }
      const p = res.prospect;
      setFirstName(p.applicant_first_name);
      if (p.has_representative || p.ss4_choice) {
        setForm((prev) => ({
          ...prev,
          has_representative: p.has_representative ? 'yes' : 'no',
          // Phase 1 pilot: SS4 upload is the only accepted path. Ignore any
          // previously saved 'email_later' / 'manual' choice.
          ss4_choice: 'upload',
        }));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const canSubmit = (() => {
    if (submitting) return false;
    if (form.has_representative === 'yes') {
      if (!form.rep_first.trim() || !form.rep_last.trim() || !form.rep_phone.trim()) {
        return false;
      }
    }
    if (!form.ss4_choice) return false;
    if (form.ss4_choice === 'upload' && !form.ss4_file) return false;
    if (form.ss4_choice === 'manual') {
      if (
        !form.legal_business_name.trim() ||
        !form.ein.trim() ||
        !form.ein_issued_date ||
        !form.business_street.trim() ||
        !form.business_city.trim() ||
        !form.business_state.trim() ||
        !form.business_zip.trim()
      ) {
        return false;
      }
    }
    if (
      !form.applicant_street.trim() ||
      !form.applicant_city.trim() ||
      !form.applicant_state.trim() ||
      !form.applicant_zip.trim()
    ) {
      return false;
    }
    return true;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setError(null);
    setSubmitting(true);

    // 1. Upload SS4 file first (if Upload path chosen)
    if (form.ss4_choice === 'upload' && form.ss4_file) {
      const upRes = await uploadSs4(form.ss4_file);
      if (!upRes.success) {
        setError(upRes.error || "Upload failed. Please try again.");
        setSubmitting(false);
        return;
      }
    }

    // 2. Submit Step 2 form
    const payload: Step2Payload = {
      applicant_street: form.applicant_street.trim(),
      applicant_city: form.applicant_city.trim(),
      applicant_state: form.applicant_state.trim(),
      applicant_zip: form.applicant_zip.trim(),
      has_representative: form.has_representative === 'yes',
      representative_first_name: form.has_representative === 'yes' ? form.rep_first.trim() : null,
      representative_last_name: form.has_representative === 'yes' ? form.rep_last.trim() : null,
      representative_phone: form.has_representative === 'yes' ? form.rep_phone.trim() : null,
      ss4_choice: form.ss4_choice as Ss4Choice,
      legal_business_name: form.ss4_choice === 'manual' ? form.legal_business_name.trim() : null,
      ein: form.ss4_choice === 'manual' ? form.ein.trim() : null,
      ein_issued_date: form.ss4_choice === 'manual' ? form.ein_issued_date : null,
      business_street: form.ss4_choice === 'manual' ? form.business_street.trim() : null,
      business_city: form.ss4_choice === 'manual' ? form.business_city.trim() : null,
      business_state: form.ss4_choice === 'manual' ? form.business_state.trim() : null,
      business_zip: form.ss4_choice === 'manual' ? form.business_zip.trim() : null,
    };

    const res = await submitStep2(payload);
    setSubmitting(false);

    if (!res.success) {
      setError(res.error || "Something went wrong. Please try again.");
      return;
    }
    navigate('/portal/payment');
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-8 text-center max-w-md mx-auto">
        <div className="inline-block w-8 h-8 border-4 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-slate-600 dark:text-slate-300 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-6 sm:p-8 max-w-2xl mx-auto">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium mb-1">
          Step 2 of 3
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-white">
          Welcome back{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-slate-600 dark:text-slate-300 mt-2">
          A few details about your business so we can register you for messaging compliance.
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SECTION A — Whose business? */}
        <section>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Whose business is this?</h2>
          <div className="space-y-2">
            <RadioOption
              checked={form.has_representative === 'no'}
              onChange={() => setForm({ ...form, has_representative: 'no' })}
              label="Mine"
              description="I'm the business owner."
            />
            <RadioOption
              checked={form.has_representative === 'yes'}
              onChange={() => setForm({ ...form, has_representative: 'yes' })}
              label="Partnered with the business owner"
              description="Another person owns the business; I'm registering it with their permission."
            />
          </div>
          {form.has_representative === 'yes' && (
            <div className="mt-4 pl-6 border-l-2 border-blue-200 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Owner's first name" required>
                  <input
                    type="text"
                    required
                    value={form.rep_first}
                    onChange={(e) => setForm({ ...form, rep_first: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                  />
                </Field>
                <Field label="Owner's last name" required>
                  <input
                    type="text"
                    required
                    value={form.rep_last}
                    onChange={(e) => setForm({ ...form, rep_last: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                  />
                </Field>
              </div>
              <Field label="Owner's phone" required>
                <input
                  type="tel"
                  required
                  value={form.rep_phone}
                  onChange={(e) => setForm({ ...form, rep_phone: e.target.value })}
                  placeholder="(555) 555-5555"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                />
              </Field>
            </div>
          )}
        </section>

        {/* SECTION B — SS4 upload */}
        <section className="pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-start justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Business information</h2>
            <button
              type="button"
              onClick={() => setShowSs4Info(true)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <Info className="w-3.5 h-3.5" />
              What's an SS4?
            </button>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
            Upload your EIN letter from the IRS &mdash; we'll pull the details we need directly
            from it.
          </p>

          <div className="space-y-2">
            <Ss4OptionCard
              icon={Upload}
              choice="upload"
              currentChoice={form.ss4_choice}
              title="Upload your SS4"
              subtitle="Recommended — fastest and most accurate."
              onSelect={() => setForm({ ...form, ss4_choice: 'upload' })}
              primary
            >
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => setForm({ ...form, ss4_file: e.target.files?.[0] || null })}
                className="block w-full text-sm text-slate-700 dark:text-slate-200 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-300 dark:file:border-slate-600 file:text-sm file:bg-white dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200 file:hover:bg-slate-50 dark:file:hover:bg-slate-600 file:cursor-pointer"
              />
              {form.ss4_file && (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Selected: <span className="font-medium">{form.ss4_file.name}</span>{' '}
                  ({(form.ss4_file.size / 1024).toFixed(1)} KB)
                </p>
              )}
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">PDF, PNG, or JPEG. 10&nbsp;MB max.</p>
            </Ss4OptionCard>

            {/*
              Phase 1 pilot: the "Email it later" and "Type manually" options
              are intentionally hidden and SS4 upload is required. The backend
              still accepts all three ss4_choice values, so restoring these
              option cards is the only change needed to re-enable them.
            */}
          </div>
        </section>

        {/* SECTION C — Your billing address */}
        <section className="pt-2 border-t border-slate-100 dark:border-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Your billing address</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
            Where to send billing statements and notifications.
          </p>
          <div className="space-y-3">
            <Field label="Street" required>
              <input
                type="text"
                required
                value={form.applicant_street}
                onChange={(e) => setForm({ ...form, applicant_street: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                autoComplete="address-line1"
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="City" required>
                <input
                  type="text"
                  required
                  value={form.applicant_city}
                  onChange={(e) => setForm({ ...form, applicant_city: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                  autoComplete="address-level2"
                />
              </Field>
              <Field label="State" required>
                <select
                  required
                  value={form.applicant_state}
                  onChange={(e) => setForm({ ...form, applicant_state: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                  autoComplete="address-level1"
                >
                  <option value="">—</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="ZIP" required>
                <input
                  type="text"
                  required
                  value={form.applicant_zip}
                  onChange={(e) => setForm({ ...form, applicant_zip: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-slate-400 dark:placeholder-slate-500"
                  autoComplete="postal-code"
                />
              </Field>
            </div>
          </div>
        </section>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full px-4 py-3 bg-blue-600 dark:bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Saving…' : 'Save & continue →'}
        </button>
      </form>

      {showSs4Info && <SS4InfoModal onClose={() => setShowSs4Info(false)} />}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function RadioOption({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
}) {
  return (
    <label
      onClick={onChange}
      className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
        checked ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 flex-shrink-0"
      />
      <div className="flex-1">
        <p className="font-medium text-sm text-slate-900 dark:text-white">{label}</p>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{description}</p>
      </div>
    </label>
  );
}

function Ss4OptionCard({
  icon: Icon,
  choice,
  currentChoice,
  title,
  subtitle,
  onSelect,
  primary,
  children,
}: {
  icon: typeof Upload;
  choice: Ss4Choice;
  currentChoice: Ss4Choice | '';
  title: string;
  subtitle: string;
  onSelect: () => void;
  primary?: boolean;
  children?: React.ReactNode;
}) {
  const selected = currentChoice === choice;
  return (
    <div
      onClick={onSelect}
      className={`p-3 border rounded-md cursor-pointer transition-colors ${
        selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
          : primary
          ? 'border-slate-300 dark:border-slate-700 hover:border-blue-400'
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${selected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`} />
        <div className="flex-1">
          <p className="font-medium text-sm text-slate-900 dark:text-white flex items-center gap-2">
            {title}
            {primary && !selected && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                Recommended
              </span>
            )}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{subtitle}</p>
        </div>
        <input
          type="radio"
          checked={selected}
          onChange={onSelect}
          className="mt-1 flex-shrink-0"
        />
      </div>
      {selected && children && (
        <div className="mt-3 pl-8">
          {children}
        </div>
      )}
    </div>
  );
}
